import React, {useEffect, useState} from 'react';
import './DropzoneMultiple.scss';
import {ReactComponent as TrashIcon} from '../../../assets/icons/trash.svg';
import {urlToFile} from '../../../helpers/functions';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import FileField from './FileField/FileField';
import AvatarField from './AvatarField/AvatarField';
import AvatarPopup from '../AvatarPopup';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import {getProfileMeAvatar} from '../../../redux/actions/profile/profileActions';
import {useTranslation} from 'react-i18next';
import {LoaderMUI} from '../../../shared';

const DropzoneMultiple = ({
  onFilesAdded,
  onAvatarAdded,
  allowFormats = ['.jpg', '.png', '.jpeg'],
  maxSizeMb = 50,
  initialImageUrl = '',
  initialAvatarUrl = '',
  path = POPUPS.AVATAR_POPUP,
  disabled = false
}) => {
  const [avatar, setAvatar] = useState([]);
  const [avatarLoading, setAvatarLoading] = useState(true);
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const dispatch = useDispatch();
  const {profileMeAvatar} = useSelector((state) => state.profile);
  const {t} = useTranslation();

  const removeFile = (fileToRemove) => {
    const files = acceptedFiles.filter(
      (file) => file.path !== fileToRemove.path && file.lastModified !== fileToRemove.lastModified
    );
    setAcceptedFiles(files);
    onFilesAdded(files);
  };

  const removeAvatar = () => {
    setAvatar({});
    onAvatarAdded({});
  };

  useEffect(() => {
    const setInitialFiles = async () => {
      try {
        if (initialImageUrl) {
          const imageFile = await urlToFile(initialImageUrl, 'initial-image.png');
          imageFile.preview = URL.createObjectURL(imageFile);
          setAcceptedFiles([imageFile]);
          onFilesAdded([imageFile]);
          handleLoadingDisable();
        } else if (initialAvatarUrl) {
          const avatarFile = await urlToFile(initialAvatarUrl, 'initial-avatar.png');
          avatarFile.preview = URL.createObjectURL(avatarFile);
          setAvatar([avatarFile]);
          onAvatarAdded([avatarFile]);
          handleLoadingDisable();
        } else {
          handleLoadingDisable();
        }
      } catch (error) {
        handleLoadingDisable();
      }
    };

    setInitialFiles();
  }, [initialImageUrl, initialAvatarUrl]);

  useEffect(() => {
    if (!profileMeAvatar.length) {
      dispatch(getProfileMeAvatar());
    }
  }, [dispatch]);

  useEffect(() => {
    if (Object.keys(avatar).length) {
      onAvatarAdded(avatar);
    }
  }, [avatar]);

  const handleLoadingDisable = () => {
    setTimeout(() => {
      setAvatarLoading(false);
    }, 250);
  };

  return (
    <div className={'dropzone-multiple'}>
      <div className={`dropzone-multiple__wrapper`}>
        {avatarLoading ? (
          <LoaderMUI noBg={true} staticPos={true} />
        ) : (
          <>
            <AvatarField
              acceptedFiles={acceptedFiles}
              avatar={avatar}
              avatarText={t('DropzoneMultiple.avatarText')}
              path={path}
              disabled={disabled}
            />
            <FileField
              avatar={avatar}
              allowFormats={allowFormats}
              acceptedFiles={acceptedFiles}
              dropText={t('DropzoneMultiple.dropText')}
              maxSizeMb={maxSizeMb}
              setAcceptedFiles={setAcceptedFiles}
              onFilesAdded={onFilesAdded}
              disabled={disabled}
            />
          </>
        )}
        {!avatarLoading && acceptedFiles.length > 0 && (
          <div className='dropzone-multiple__trash' onClick={() => removeFile(acceptedFiles[0])}>
            <TrashIcon />
            {t('DropzoneMultiple.delete')}
          </div>
        )}
        {!avatarLoading && avatar && Object.keys(avatar).length > 0 && (
          <div className='dropzone-multiple__trash avatar-trash' onClick={() => removeAvatar(avatar[0])}>
            <TrashIcon />
            {t('DropzoneMultiple.delete')}
          </div>
        )}
      </div>
      <div className='dropzone-multiple-notation'>
        {t('DropzoneMultiple.formats')}: {allowFormats.join(', ')}&nbsp;{t('DropzoneMultiple.maxSize')}. {maxSizeMb} МБ.
      </div>

      <AvatarPopup path={path} setAvatar={setAvatar} avatar={avatar} images={profileMeAvatar} />
    </div>
  );
};

DropzoneMultiple.propTypes = {
  maxSizeMb: PropTypes.number,
  maxFiles: PropTypes.number,
  dropText: PropTypes.string,
  onFilesAdded: PropTypes.func,
  allowFormats: PropTypes.array,
  initialImageUrl: PropTypes.string, // Пропс для начального изображения
  initialAvatarUrl: PropTypes.string // Пропс для начального аватара
};

export default DropzoneMultiple;
